import { useRef, useEffect } from 'react'
import { includes } from 'lodash'
import { parse } from 'query-string'

import { CheckoutError, RecurlyPayment, processCheckout } from 'api'
import {
  AIWRITER_BASE_URL,
  CHECKOUT_ERRORS,
  GENERIC_RECURLY_ERRORS,
  getCheckoutURL,
  NOT_TRIAL_PLANS,
  PROMO_LABEL_VALUE,
} from 'utils/consts.mjs'
import { Mixpanel, MIXPANEL_PROPS } from 'utils/mixpanel'
import { EVENT_ACTIONS } from 'utils/tracking'
import { getLanguageCode, translate } from 'utils/translation'
import { sendMessage } from 'utils/functions'
import { useRecurly } from 'lib/recurly'
import { useUser } from 'hooks/use-user'
import HubSpotService from 'utils/hubspot'
import { getAddonPrice, getCurrencyAndRegion } from 'utils/payment'
import { unformatNumber } from 'utils/numbers'
import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'
import { getBuyPurchaseType } from 'utils/tracking/calculate-checkout-properties'
import { handleThreeDSecure } from 'features/checkout/utils'
import { RecurlyErrorResponse } from 'lib/recurly/types'
import { useContextSave } from 'hooks/use-context-save'

import { THREE_D_ELEMENT_ID } from '../../components/form/consts'
import { RecurlyContext } from '../../providers/index'

interface HandleCheckoutParams {
  token: RecurlyPayment
  threeDSecureToken?: string
  coupon?: string
  addOn?: string
  noTrial?: boolean
  skipPreCheckout?: boolean
  paymentMethod?: string
  checkoutSuccessSource: string
}

export const useCheckout = () => {
  const context = useContextSave(RecurlyContext)
  const {
    setIsLoading,
    interval,
    tier,
    onContinue,
    onBlockPayment,
    currentSubscription,
    hasAIWAddon,
    addonDetail,
    setShowThreeDAuth,
    setServerError,
  } = context

  const {
    next = '',
    source = '',
    aiwAddon: rawAiwAddon = '',
  } = parse(window.location.search)

  const aiwAddon = Array.isArray(rawAiwAddon) ? rawAiwAddon[0] : rawAiwAddon

  const recurly = useRecurly()
  const user = useUser()

  const abandonStatus = useRef({ status: true })

  let observer: MutationObserver | null = null

  const addObserver = () => {
    observer = new MutationObserver(() => {
      const iframe = document.querySelector(
        `#${THREE_D_ELEMENT_ID} > div > iframe`,
      )
      if (iframe && iframe.getAttribute('scrolling') === 'no') {
        iframe.setAttribute('scrolling', 'yes')
      }
    })

    const container = document.getElementById(THREE_D_ELEMENT_ID)
    if (!container) return
    observer.observe(container, {
      subtree: true,
      childList: true,
    })
  }

  const removeObserver = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  const handleCheckout = async (params: HandleCheckoutParams) => {
    const {
      token,
      threeDSecureToken = undefined,
      coupon = undefined,
      noTrial = false,
      skipPreCheckout = false,
      paymentMethod,
      checkoutSuccessSource = '',
    } = params
    const mixpanelProps = {
      tier,
      planInterval: interval,
      paymentMethod: paymentMethod || token.type,
      subscription: currentSubscription,
      source: checkoutSuccessSource,
    }

    setServerError(null)
    setIsLoading(true)

    if (!currentSubscription?.value) {
      throw new Error('Subscription plan is not defined')
    }

    try {
      const eventId = crypto.randomUUID()
      const isNoTrial =
        noTrial || NOT_TRIAL_PLANS.includes(currentSubscription?.plan || '')
      const res = await processCheckout({
        paymentMethod: token,
        plan: currentSubscription?.value,
        currency: currentSubscription.currency,
        threeDSecureToken,
        coupon,
        addOn: hasAIWAddon && addonDetail?.code ? addonDetail?.code : undefined,
        noTrial: isNoTrial,
        item: currentSubscription?.itemCode,
        skipPreCheckout,
        eventId,
      })
      if (res.error) {
        // @ts-ignore  TODO: Wait for TS component update
        Mixpanel.trackPaymentSubmitClick({
          ...mixpanelProps,
          paymentStatus: MIXPANEL_PROPS.PAYMENT_STATUS.values.PAYMENT_ISSUE,
          paymentIssue: `${res.error}`,
        })
        throw res.error
      } else {
        // @ts-ignore  TODO: Wait for TS component update
        Mixpanel.trackPaymentSubmitClick({
          ...mixpanelProps,
          paymentStatus: MIXPANEL_PROPS.PAYMENT_STATUS.values.SUCCESS,
        })

        const eventKey = `${tier}_${interval}` as const

        if (aiwAddon) {
          HubSpotService.instance.setTrackCustomBehavioralEvent({
            name: 'pe25525864_purchase_ubs___aiw__new_',
          })
          const { planCode } = getCurrencyAndRegion(user.countryCode)
          const price = addonDetail
            ? `${getAddonPrice(
                addonDetail,
                currentSubscription.currency,
                planCode,
              )}`
            : '0'
          Mixpanel.trackAiwCheckoutSuccess({ addonType: aiwAddon, price }, user)
          trackEvent({
            event: isNoTrial ? EVENTS.CHECKOUT_SUCCESS : EVENTS.START_TRIAL,
            mixpanelProperties: [
              {
                ...mixpanelProps,
                subscription: currentSubscription,
                source: MIXPANEL_PROPS.SOURCE.values.AIW_PRICING_PAGE,
                purchaseType: MIXPANEL_PROPS.PURCHASE_TYPE.values.WIN,
              },
            ],
            gtmProperties: {
              eventCategory: 'purchaseCompleted',
              eventAction:
                EVENT_ACTIONS.checkout[
                  eventKey as keyof typeof EVENT_ACTIONS.checkout
                ],
              eventLabel: 'Success',
              value: unformatNumber(currentSubscription.unitPrice),
              transaction_id: eventId,
              currency: currentSubscription.currency,
              userData: {
                email: user.email,
                externalId: user.id,
              },
              eventId,
            },
          })
        } else {
          trackEvent({
            event: isNoTrial ? EVENTS.CHECKOUT_SUCCESS : EVENTS.START_TRIAL,
            mixpanelProperties: [
              {
                ...mixpanelProps,
                subscription: currentSubscription,
                purchaseType: getBuyPurchaseType(
                  currentSubscription.plan,
                  noTrial,
                ),
              },
            ],
            gtmProperties: {
              eventCategory: 'purchaseCompleted',
              eventAction:
                EVENT_ACTIONS.checkout[
                  eventKey as keyof typeof EVENT_ACTIONS.checkout
                ],
              eventLabel: 'Success',
              value: unformatNumber(currentSubscription.unitPrice),
              transaction_id: eventId,
              currency: currentSubscription.currency,
              userData: {
                email: user.email,
                externalId: user.id,
              },
              eventId,
            },
          })
        }

        if (
          !NOT_TRIAL_PLANS.includes(interval) &&
          tier !== 'restart' &&
          !user.free_trial_used &&
          !hasAIWAddon
        ) {
          HubSpotService.instance.setTrackCustomBehavioralEvent({
            name: 'pe25525864_third_day_free_trial__new_',
          })
        }

        if (currentSubscription.planLabel === PROMO_LABEL_VALUE)
          sessionStorage.setItem('hatp', 'true')

        if (onContinue) {
          onContinue(() => {
            setIsLoading(false)
          })
        } else {
          // TODO: redirect to the right page
          // Should not clear token in this case
          // clearToken()
          let nextHref

          if (hasAIWAddon && next) {
            nextHref = next
          } else if (hasAIWAddon) {
            nextHref = `${AIWRITER_BASE_URL}?signin=true`
          } else {
            nextHref = next
          }

          sendMessage('SUBSCRIPTION_UPGRADED', {
            next: nextHref,
            tier,
            plan: currentSubscription?.value,
            paymentMethod: token.type,
            hasAIWAddon,
            promo: false,
            utmParams: {
              source: source ?? 'ubs-check-out-page',
              medium: 'post-check-out-modal',
            },
          })
        }
        setIsLoading(false)
        abandonStatus.current.status = false
      }
    } catch (err) {
      const customError = err as CheckoutError
      setIsLoading(false)
      let errorMessage = translate('card_error')
      let userNotConfirmed = false

      if (customError.response) {
        const {
          code,
          description,
          error: reqError,
          email,
        } = customError.response.data

        if (code === CHECKOUT_ERRORS.THREE_D_SECURE_ACTION_REQUIRED) {
          return handleThreeDSecure({
            recurly,
            error: err as RecurlyErrorResponse,
            removeObserver,
            token,
            mixpanelProps,
            addObserver,
            setShowThreeDAuth,
            setIsLoading,
            setServerError,
            checkoutSuccessSource,
            handleRequest: handleCheckout,
          })
        }
        if (code === CHECKOUT_ERRORS.BLOCKED_PAYMENT_METHOD) {
          // @ts-ignore  TODO: Wait for TS component update
          Mixpanel.trackPaymentSubmitClick({
            ...mixpanelProps,
            paymentStatus: MIXPANEL_PROPS.PAYMENT_STATUS.values.PAYMENT_ISSUE,
            paymentIssue: `${code}: ${description?.error}`,
            code,
            message: description?.error,
          })
          onBlockPayment?.({
            token,
            isOpen: true,
            data: {
              paymentMethod: token.type,
              subscriptionPlan: currentSubscription?.value,
              error: code,
              threeDSecureToken,
              paymentSource: paymentMethod,
            },
          })
          return
        }

        if (code === 'active_subscription') {
          // @ts-ignore  TODO: Wait for TS component update
          Mixpanel.trackPaymentSubmitClick({
            ...mixpanelProps,
            paymentStatus: MIXPANEL_PROPS.PAYMENT_STATUS.values.ERROR,
            paymentError: `${code}: ${
              reqError || translate('active_subscription', email)
            }`,
            code,
            message: reqError,
          })
          return setServerError({
            type: 'error',
            message: translate('active_subscription', user.email),
            code,
          })
        }

        // todo: active subscription error handle
        if (!includes(GENERIC_RECURLY_ERRORS, code))
          errorMessage = description?.error || errorMessage

        if (code === 'user_not_confirmed') {
          userNotConfirmed = true
        }

        // @ts-ignore  TODO: Wait for TS component update
        Mixpanel.trackPaymentSubmitClick({
          ...mixpanelProps,
          paymentStatus: includes(GENERIC_RECURLY_ERRORS, code)
            ? MIXPANEL_PROPS.PAYMENT_STATUS.values.PAYMENT_ISSUE
            : MIXPANEL_PROPS.PAYMENT_STATUS.values.ERROR,
          paymentIssue: `${code}: ${description?.error}`,
          code,
          message: description?.error,
        })
      } else {
        // @ts-ignore  TODO: Wait for TS component update
        Mixpanel.trackPaymentSubmitClick({
          ...mixpanelProps,
          paymentStatus: MIXPANEL_PROPS.PAYMENT_STATUS.values.ERROR,
          paymentError: `Unknown error`,
        })
      }

      if (userNotConfirmed) {
        sendMessage('SHOW_EMAIL_CONFIRM_MODAL', {
          email: user.email,
        })
      } else {
        setServerError({
          type: 'error',
          message: errorMessage,
          code: customError?.response?.data?.code,
        })
      }
      setServerError({
        type: 'error',
        message: 'request_failed',
      })
      setIsLoading(false)
    }
  }

  useEffect(() => {
    abandonStatus.current = { status: true }

    // returned function will be called on component unmount
    return () => {
      if (abandonStatus.current?.status === true) {
        HubSpotService.instance.setTrackCustomBehavioralEvent({
          name: 'pe25525864_abandoned_checkout_page',
          properties: {
            hs_country: user.country,
            hs_city: user.city,
            hs_page_url: `${window.location.host}${getCheckoutURL({
              lang: getLanguageCode(),
            })}`,
          },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { handleCheckout }
}
